import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from '@unform/web';
import { SubmitHandler } from '@unform/core';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { Button, Header, Input, Loading, Modal } from '../../components';
import { ReactComponent as PrinterIcon } from '../../assets/icons/printer.svg';
import { ReactComponent as InfoIcon } from '../../assets/icons/info.svg';
// import { ReactComponent as ChevronIcon } from '../../assets/icons/chevron.svg';
import Info from './components/Info';
import api from '../../services/api';
import { usePDFData } from '../../hooks/usePDFData';
import { useAuth } from '../../hooks/useAuth';
import Tracking from './components/Tracking';
import * as S from './styles';

interface FormDataFilters {
  from: string;
  to: string;
  os_number: string;
  customer_name: string;
  salesman: string;
  ophthalmologist: string;
  page: number;
  limit: number;
}

interface PreOS {
  id: number;
  os_number: string;
  created_at: string;
  customer_name: string;
  customer_contact: string;
  product: {
    description: string;
  };
  manufacturer: {
    name: string;
  };
  salesman: string;
  ophthalmologist: string;
  customer_certified_name: string;
  prevision?: string;
  status: string;
  register_by_name: string;
  esf_right_eye: number;
  cil_right_eye: number;
  eixo_right_eye: number;
  add_right_eye: number;
  dnp_right_eye: number;
  esf_left_eye: number;
  cil_left_eye: number;
  eixo_left_eye: number;
  add_left_eye: number;
  dnp_left_eye: number;
  horizontal: number;
  vertical: number;
  po: number;
  diagonal: number;
  height: number;
  model: string;
  frame: string;
  reference?: string;
  mounting: number;
  antiscratch: number;
  coloring: number;
  coloring_type?: string;
  antireflection: number;
  antireflection_type?: string;
  observations?: string;
  print_prisma: number;
  right_base: number;
  right_base_quantity: number;
  left_base: number;
  left_base_quantity: number;
}

export interface TrackingModel {
  status: string;
  start: string;
  end: string;
}

const SearchOS = () => {
  const { setPdfData } = usePDFData();
  const { user } = useAuth();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTracking, setIsLoadingTracking] = useState(false);
  const [modalIsOpen, setShowModal] = useState(false);
  const [modalInfoIsOpen, setShowInfoModal] = useState(false);
  const [selectedPreOS, setSelectedPreOS] = useState<PreOS>();
  const [preOS, setPreOS] = useState<PreOS[]>([]);
  const [totalPreOS, setTotalPreOS] = useState(0);
  const [preOSTracking, setPreOSTracking] = useState<TrackingModel[] | null>(
    null,
  );

  const limit = 5;
  const visibleButtons = 5;
  const [totalPages, setTotalPages] = useState(Math.ceil(totalPreOS / limit));
  const [filters, setFilters] = useState<FormDataFilters>({
    from: '',
    to: '',
    os_number: '',
    customer_name: '',
    ophthalmologist: '',
    salesman: '',
    page: 1,
    limit,
  });

  const makePaginationText = useMemo(() => {
    const startItems =
      totalPreOS >= filters.page
        ? filters.page * limit - limit + 1
        : totalPreOS;
    let endItems = totalPreOS >= limit ? filters.page * limit : totalPreOS;
    endItems = endItems > totalPreOS ? totalPreOS : endItems;
    const text = `Mostrando ${startItems} até ${endItems} de ${totalPreOS} registro(s)`;

    return text;
  }, [totalPreOS, filters.page]);

  const calculateVisibleButtons = useCallback(() => {
    let maxLeft = filters.page - Math.floor(visibleButtons / 2);
    let maxRight = filters.page + Math.floor(visibleButtons / 2);
    const roundedTotalPages = Math.ceil(totalPages);

    if (maxLeft < 1) {
      maxLeft = 1;
      maxRight = visibleButtons;
    }

    if (maxRight > roundedTotalPages) {
      maxLeft = roundedTotalPages - (visibleButtons - 1);
      maxRight = roundedTotalPages;

      if (maxLeft < 1) maxLeft = 1;
    }

    return { maxLeft, maxRight };
  }, [filters.page, totalPages]);

  const goToPage = useCallback(
    (page: number) => {
      if (page > Math.ceil(totalPages)) return;

      setFilters({
        ...filters,
        page,
      });
    },
    [filters, totalPages],
  );

  const renderPageButtons = useMemo(() => {
    const { maxLeft, maxRight } = calculateVisibleButtons();
    // const isButtonDisabled = maxRight <= visibleButtons;
    const buttons = [];

    // buttons.push(
    //   <S.PageButton
    //     key="leftArrow"
    //     type="button"
    //     title="Ver páginas anteriores"
    //     disabled={isButtonDisabled}
    //   >
    //     <ChevronIcon width="10" height="10" />
    //   </S.PageButton>,
    // );

    for (let page = maxLeft; page <= maxRight; page += 1) {
      buttons.push(
        <S.PageButton
          type="button"
          className={filters.page === page ? 'active' : ''}
          title={`Ver página ${page}`}
          key={`page${page}`}
          onClick={() => goToPage(page)}
        >
          {page}
        </S.PageButton>,
      );
    }

    // buttons.push(
    //   <S.PageButton
    //     key="RightArrow"
    //     type="button"
    //     title="Ver próximas páginas"
    //     reverse
    //     disabled={isButtonDisabled}
    //   >
    //     <ChevronIcon width="10" height="10" />
    //   </S.PageButton>,
    // );

    return buttons;
  }, [calculateVisibleButtons, filters.page, goToPage]);

  const loadPresOS = useCallback(
    async (selectedFilters?: FormDataFilters) => {
      setIsLoading(true);
      try {
        const response = await api.get<{ data: PreOS[]; total: number }>(
          'pre-os',
          {
            params: {
              ...filters,
              ...selectedFilters,
            },
          },
        );

        setPreOS(response.data.data);
        setTotalPreOS(response.data.total);
        setTotalPages(response.data.total / limit);
      } catch (error) {
        toast.error('Não foi possível consultar os dados. Tente mais tarde.');
      } finally {
        setIsLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    loadPresOS();
  }, [loadPresOS]);

  const toggleModal = () => {
    setShowModal(oldState => !oldState);
  };

  const toggleInfoModal = (selected: PreOS) => {
    setPreOSTracking(null);
    setSelectedPreOS(selected);
    setShowInfoModal(oldState => !oldState);
  };

  const handleSelectedFilters: SubmitHandler<FormDataFilters> = async formData => {
    setShowModal(oldState => !oldState);
    setFilters({
      ...filters,
      ...formData,
      page: 1,
    });
  };

  const handleOpenPDF = (selected: PreOS) => {
    setPdfData(selected);
    history.push(`${process.env.PUBLIC_URL}/pdf`);
  };

  const handleLoadTracking = async () => {
    setIsLoadingTracking(true);
    try {
      const response = await api.get<
        Array<{ setor: string; data_hora: string; data_hora_fim: string }>
      >('/os/tracking', {
        params: {
          os_number: selectedPreOS?.os_number,
          register_by: user.login,
        },
      });

      setPreOSTracking(
        response.data.map(item => ({
          status: item.setor,
          start: item.data_hora,
          end: item.data_hora_fim,
        })),
      );
    } catch (error) {
      toast.error('Não foi possível atualizar o rastreio');
    } finally {
      setIsLoadingTracking(false);
    }
  };

  return (
    <>
      <Header />
      <S.Container>
        <Button
          title="Configurar filtro para listagem"
          text="Filtrar"
          onClick={toggleModal}
        />

        <S.TableHeader>
          <span>OS</span>
          <span>Cliente</span>
          <span>Produto</span>
          <span>Fabricante</span>
          <span>Cadastro</span>
          <span>Previsão</span>
          <span>Status</span>
          <span> </span>
        </S.TableHeader>

        <S.TableBody>
          {isLoading ? (
            <S.EmptyContent>
              <Loading color="#aaa" />
            </S.EmptyContent>
          ) : (
            <>
              {preOS.length > 0 ? (
                preOS.map(row => (
                  <S.TableRow key={row.id}>
                    <span>{row.os_number}</span>
                    <span>{row.customer_name}</span>
                    <span>{row.product.description}</span>
                    <span>{row.manufacturer.name}</span>
                    <span>
                      {new Date(row.created_at).toLocaleString('pt-BR')}
                    </span>
                    <span>
                      {row.prevision
                        ? new Date(row.prevision).toLocaleDateString('pt-BR')
                        : 'N/A'}
                    </span>
                    <span>{row.status}</span>
                    <S.IconsContainer>
                      <PrinterIcon
                        width="20"
                        height="20"
                        title="Imprimir"
                        onClick={() => handleOpenPDF(row)}
                      />
                      <InfoIcon
                        width="20"
                        height="20"
                        title="Ver detalhes"
                        onClick={() => toggleInfoModal(row)}
                      />
                    </S.IconsContainer>
                  </S.TableRow>
                ))
              ) : (
                <S.EmptyContent>Nenhuma pre-os encontrada</S.EmptyContent>
              )}
            </>
          )}
        </S.TableBody>

        <S.PaginationContainer>
          <span>{makePaginationText}</span>
          <S.PageButtonsContainer>{renderPageButtons}</S.PageButtonsContainer>
        </S.PaginationContainer>
        <Modal modalIsOpen={modalIsOpen} setShowModal={setShowModal} size="md">
          <Form onSubmit={handleSelectedFilters}>
            <S.RowsGrid>
              <S.InputColumnGroupGrid cols={2}>
                <Input label="De" name="from" type="date" />
                <Input label="Até" name="to" type="date" />
              </S.InputColumnGroupGrid>
              <Input label="Número da OS" name="os_number" type="text" />
              <Input label="Cliente" name="customer_name" type="text" />
              <Input label="Vendedor" name="salesman" type="text" />
              <Input
                label="Oftalmologista"
                name="ophthalmologist"
                type="text"
              />
              <Button
                text="Confirmar filtros"
                type="submit"
                containerStyle={{ marginTop: '1rem' }}
              />
            </S.RowsGrid>
          </Form>
        </Modal>
        <Modal
          modalIsOpen={modalInfoIsOpen}
          setShowModal={setShowInfoModal}
          size="lg"
        >
          {selectedPreOS && (
            <S.RowsGrid>
              <Info label="OS:" value={selectedPreOS.os_number} />
              <S.InputColumnGroupGrid cols={2}>
                <Info label="Cliente:" value={selectedPreOS.customer_name} />
                <Info
                  label="Telefone:"
                  value={selectedPreOS.customer_contact}
                />
              </S.InputColumnGroupGrid>
              <Info
                label="Produto:"
                value={selectedPreOS.product.description}
              />
              <Info
                label="Fabricante:"
                value={selectedPreOS.manufacturer.name}
              />
              <Info label="Vendedor:" value={selectedPreOS.salesman} />
              <S.InputColumnGroupGrid cols={2}>
                <Info
                  label="Oftalmologista:"
                  value={selectedPreOS.ophthalmologist}
                />
                <Info
                  label="Montagem:"
                  value={selectedPreOS.mounting === 1 ? 'Sim' : 'Não'}
                />
              </S.InputColumnGroupGrid>
              <S.InputColumnGroupGrid cols={2}>
                <Info
                  label="Cliente certificado:"
                  value={selectedPreOS.customer_certified_name}
                />
                <Info
                  label="Coloração:"
                  value={selectedPreOS.coloring_type || 'N/A'}
                />
              </S.InputColumnGroupGrid>
              <S.InputColumnGroupGrid cols={2}>
                <Info
                  label="Data de cadastro:"
                  value={new Date(selectedPreOS.created_at).toLocaleString(
                    'pt-BR',
                  )}
                />
                <Info
                  label="Antirrisco:"
                  value={selectedPreOS.antiscratch === 1 ? 'Sim' : 'Não'}
                />
              </S.InputColumnGroupGrid>
              <S.InputColumnGroupGrid cols={2}>
                <Info
                  label="Previsão:"
                  value={
                    selectedPreOS.prevision
                      ? new Date(selectedPreOS.prevision).toLocaleDateString(
                          'pt-BR',
                        )
                      : 'N/A'
                  }
                />
                <Info
                  label="Antirreflexo:"
                  value={selectedPreOS.antireflection_type || 'N/A'}
                />
              </S.InputColumnGroupGrid>
              <S.InputColumnGroupGrid cols={2}>
                <Info label="Status:" value={selectedPreOS.status} />
                <S.ShowTrackingButton
                  text={isLoadingTracking ? <Loading /> : 'Ver rastreio'}
                  disabled={isLoadingTracking}
                  onClick={handleLoadTracking}
                />
              </S.InputColumnGroupGrid>
              {preOSTracking && (
                <Tracking
                  titles={['Status', 'Início', 'Fim']}
                  data={preOSTracking}
                />
              )}
              <Info
                label="Observação:"
                value={selectedPreOS.observations || ''}
                orientation="column"
              />
            </S.RowsGrid>
          )}
        </Modal>
      </S.Container>
    </>
  );
};

export default SearchOS;
